import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BellIcon, ShoppingBagIcon, CubeIcon, CogIcon, TrashIcon } from '@heroicons/react/24/outline';
import type { NotificationPublic } from '@wooconnect/shared';
import { useUIStore } from '@/stores/uiStore';
import { useAuthStore } from '@/stores/authStore';
import { useNotifications, useUnreadCount, useMarkRead, useMarkAllRead, useDeleteAllNotifications } from '@/hooks/useNotifications';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Skeleton } from '@/components/ui/Skeleton';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { cn } from '@/utils/cn';
import { formatRelativeTime } from '@/utils/format';
import { translateLegacyNotification } from '@/utils/notificationTranslation';

type TabFilter = 'all' | 'unread' | 'orders' | 'stock' | 'system';

export function NotificationsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const activeSiteId = useUIStore((s) => s.activeSiteId);
  const sites = useAuthStore((s) => s.sites);
  const [tab, setTab] = useState<TabFilter>('all');
  const [siteFilter, setSiteFilter] = useState<string>('');
  const [deleteAllOpen, setDeleteAllOpen] = useState(false);

  const { data: notifications, isLoading, isError, refetch } = useNotifications(tab, siteFilter || null);
  const { data: unreadData } = useUnreadCount();
  const markRead = useMarkRead();
  const markAllRead = useMarkAllRead();
  const deleteAll = useDeleteAllNotifications();

  const tabs: Array<{ key: TabFilter; label: string }> = [
    { key: 'all', label: t('common.all') },
    { key: 'unread', label: t('notifications.unread') },
    { key: 'orders', label: t('notifications.orderNotifications') },
    { key: 'stock', label: t('notifications.stockNotifications') },
    { key: 'system', label: t('notifications.systemNotifications') },
  ];

  const iconMap: Record<string, typeof BellIcon> = {
    new_order: ShoppingBagIcon,
    order_status_changed: ShoppingBagIcon,
    refund_request: ShoppingBagIcon,
    low_stock: CubeIcon,
    product_backorder: CubeIcon,
    system: CogIcon,
    ai_insight: CogIcon,
    subscription_expiring: CogIcon,
    subscription_expired: CogIcon,
    plugin_required: CogIcon,
    bridge_offline: CogIcon,
  };

  const setActiveSite = useUIStore((s) => s.setActiveSite);

  const handleClick = (notif: NotificationPublic) => {
    if (!notif.isRead) markRead.mutate(notif.id);
    // Switch store if notification is for a different store
    if (notif.siteId && notif.siteId !== activeSiteId) {
      const matchedSite = sites.find((s) => s.id === notif.siteId);
      if (matchedSite) setActiveSite(matchedSite);
    }
    if (notif.resourceType === 'order' && notif.resourceId) navigate(`/orders/${notif.resourceId}`);
    else if (notif.resourceType === 'product' && notif.resourceId) navigate(`/products/${notif.resourceId}`);
  };

  const handleDeleteAll = () => {
    deleteAll.mutate(siteFilter || undefined);
    setDeleteAllOpen(false);
  };

  const unreadCount = unreadData?.total ?? 0;

  const renderNotification = (notif: NotificationPublic) => {
    const data = notif.data as Record<string, unknown> | undefined;
    let displayTitle = data?.titleKey
      ? t(data.titleKey as string, (data.titleParams ?? {}) as Record<string, string>)
      : notif.title;
    let displayBody = data?.bodyKey
      ? t(data.bodyKey as string, (data.bodyParams ?? {}) as Record<string, string>)
      : notif.body;

    // Legacy English notification translation fallback
    if (!data?.titleKey && displayTitle) {
      const translated = translateLegacyNotification(displayTitle, displayBody ?? '', t);
      displayTitle = translated.title;
      displayBody = translated.body;
    }

    return (
      <div key={notif.id} onClick={() => handleClick(notif)} className={cn('cursor-pointer rounded-xl border p-4 transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/50', notif.isRead ? 'border-gray-100 dark:border-gray-800' : 'border-brand-200 bg-brand-50/30 dark:border-brand-900/30 dark:bg-brand-900/10')}>
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 pt-0.5">
            {!notif.isRead && <span className="mr-2 inline-block h-2 w-2 rounded-full bg-brand-500" />}
            {(() => { const Icon = iconMap[notif.type] ?? BellIcon; return <Icon className="inline h-5 w-5 text-gray-400" />; })()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between">
              <p className={cn('text-sm font-medium', notif.isRead ? 'text-gray-700 dark:text-gray-300' : 'text-gray-900 dark:text-gray-100')}>{displayTitle}</p>
              <span className="ml-2 flex-shrink-0 text-xs text-gray-400">{formatRelativeTime(notif.createdAt)}</span>
            </div>
            {displayBody && <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">{displayBody}</p>}
            {notif.siteName && (
              <span className="mt-1 inline-block rounded-full bg-brand-100 px-2 py-0.5 text-xs font-medium text-brand-700 dark:bg-brand-900/30 dark:text-brand-300">
                {notif.siteName}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('notifications.title')}</h1>
        <div className="flex gap-2">
          {unreadCount > 0 && (
            <Button variant="ghost" size="sm" onClick={() => markAllRead.mutate(siteFilter || undefined)} loading={markAllRead.isPending}>
              {t('notifications.markAllRead')}
            </Button>
          )}
          {notifications && notifications.length > 0 && (
            <Button variant="ghost" size="sm" onClick={() => setDeleteAllOpen(true)} className="text-red-600 hover:text-red-700 dark:text-red-400">
              <TrashIcon className="mr-1 h-4 w-4" />
              {t('notifications.deleteAll')}
            </Button>
          )}
        </div>
      </div>

      {/* Store filter */}
      {sites.length > 1 && (
        <select
          value={siteFilter}
          onChange={(e) => setSiteFilter(e.target.value)}
          className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"
        >
          <option value="">{t('notifications.allStores')}</option>
          {sites.map((site) => (
            <option key={site.id} value={site.id}>{site.name}</option>
          ))}
        </select>
      )}

      {/* Tabs */}
      <div className="flex gap-1 rounded-lg bg-gray-100 p-1 dark:bg-gray-800">
        {tabs.map((t_) => (
          <button key={t_.key} onClick={() => setTab(t_.key)} className={cn('rounded-md px-3 py-1.5 text-sm font-medium transition-colors', tab === t_.key ? 'bg-white shadow-sm dark:bg-gray-700 dark:text-gray-100' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200')}>
            {t_.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="space-y-3">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-20 w-full rounded-xl" />)}</div>
      ) : isError ? (
        <ErrorState onRetry={() => refetch()} />
      ) : notifications && notifications.length > 0 ? (
        <div className="space-y-2">
          {notifications.map(renderNotification)}
        </div>
      ) : (
        <EmptyState icon={BellIcon} title={t('notifications.noNotifications')} />
      )}

      <ConfirmDialog
        isOpen={deleteAllOpen}
        onClose={() => setDeleteAllOpen(false)}
        onConfirm={handleDeleteAll}
        title={t('notifications.deleteAll')}
        message={t('notifications.confirmDeleteAll')}
        confirmVariant="danger"
        confirmLabel={t('notifications.deleteAll')}
        loading={deleteAll.isPending}
      />
    </div>
  );
}
